import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NovelCard from "@/components/feature/NovelCard";
import useSeo from "@/hooks/useSeo";
import { novels } from "@/mocks/narutoContent";

export default function NovelDetail() {
  const { id } = useParams();
  const novel = novels.find((n) => n.id === id) ?? novels[0];
  const [activeChapter, setActiveChapter] = useState(1);

  const chapters = useMemo(
    () =>
      Array.from({ length: Math.min(novel.chapters, 12) }, (_, i) => ({
        no: i + 1,
        title: i === 0 ? "缘起 · 木叶的风" : `第 ${i + 1} 章 · 风继续吹`,
      })),
    [novel.id, novel.chapters],
  );

  const related = novels.filter((n) => n.id !== novel.id).slice(0, 3);

  useSeo({
    title: `${novel.title} - 火影忍者同人小说 | 影页社`,
    description: novel.summary,
    keywords: `火影忍者同人,${novel.title},火影同人小说`,
    canonical: `https://hokagecell.github.io/xiaoshuo/${novel.id}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Book",
        name: novel.title,
        author: { "@type": "Person", name: novel.author },
        description: novel.summary,
        url: `https://hokagecell.github.io/xiaoshuo/${novel.id}`,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://hokagecell.github.io/" },
          { "@type": "ListItem", position: 2, name: "同人小说", item: "https://hokagecell.github.io/xiaoshuo" },
          { "@type": "ListItem", position: 3, name: novel.title, item: `https://hokagecell.github.io/xiaoshuo/${novel.id}` },
        ],
      },
    ],
  });

  return (
    <AppLayout>
      <div className="pt-20">
        <Breadcrumb items={[{ name: "同人小说", path: "/xiaoshuo" }, { name: novel.title }]} />

        {/* 小说头部 */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 pt-6 pb-10">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <img
                src={novel.cover}
                alt={`${novel.title} 火影忍者同人小说封面`}
                title={`${novel.title} · 火影忍者同人小说`}
                className="w-full rounded-lg object-cover object-top lg:max-h-[420px]"
              />
            </div>
            <div className="lg:col-span-2 flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-800">{novel.tag}</span>
                <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs text-accent-700">{novel.category}</span>
              </div>
              <h1 className="mt-4 font-display text-3xl md:text-5xl text-foreground-950 leading-tight">{novel.title}</h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">{novel.summary}</p>
              <div className="mt-5 flex flex-wrap items-center gap-5 font-label text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1.5"><i className="ri-account-circle-line text-lg text-primary-600"></i>{novel.author}</span>
                <span className="inline-flex items-center gap-1.5"><i className="ri-file-list-3-line text-lg text-primary-600"></i>{novel.chapters} 章 · {novel.words}</span>
                <span className="inline-flex items-center gap-1.5"><i className="ri-eye-line text-lg text-primary-600"></i>{novel.views} 阅读</span>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line"></i> 开始阅读
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-accent-300 px-7 py-3 font-label text-sm whitespace-nowrap text-accent-700 transition-colors hover:bg-accent-500 hover:text-background-50"
                >
                  <i className="ri-heart-3-line"></i> 收藏 {novel.likes}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 章节列表 */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12">
            <h2 className="font-display text-2xl md:text-3xl text-foreground-950">目录 · 章节列表</h2>
            <p className="mt-2 text-sm text-foreground-600">点击章节可预览，完整内容持续连载更新中。</p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {chapters.map((c) => {
                const selected = activeChapter === c.no;
                return (
                  <button
                    key={c.no}
                    type="button"
                    onClick={() => setActiveChapter(c.no)}
                    className={`flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
                      selected
                        ? "border-primary-400 bg-primary-500 text-background-50"
                        : "border-background-200 bg-background-50 text-foreground-800 hover:border-primary-300"
                    }`}
                  >
                    <span className="font-label text-sm">
                      <span className={selected ? "text-primary-200" : "text-primary-600"}>#{c.no}</span> {c.title}
                    </span>
                    <i className={`ri-bookmark-line ${selected ? "text-primary-200" : "text-foreground-400"}`}></i>
                  </button>
                );
              })}
            </div>
            {novel.chapters > 12 && (
              <p className="mt-4 font-label text-xs text-foreground-500">已展示前 12 章，更多章节持续更新中……</p>
            )}
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="font-display text-lg text-foreground-950">
                第 {activeChapter} 章 · {chapters[activeChapter - 1]?.title}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                “{novel.summary}” 本章内容正在用心打磨中，欢迎收藏追更，后续章节上线后会第一时间更新。感谢每一位同好的陪伴。
              </p>
            </div>
          </div>
        </section>

        {/* 相关推荐 */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 pb-16">
          <h2 className="mb-6 font-display text-2xl md:text-3xl text-foreground-950">猜你喜欢 · 同人小说推荐</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((n) => (
              <NovelCard key={n.id} novel={n} />
            ))}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}